import type { Private } from '@seek/indie-api-types';
import {
  Badge,
  Box,
  ButtonIcon,
  Dialog,
  IconDelete,
  IconMobile,
  IconSecurity,
  Inline,
  Stack,
  TableCell,
  TableRow,
  Text,
  useToast,
} from 'braid-design-system';
import { useId, useState } from 'react';

import { DateRenderer } from './components/DateRenderer/DateRenderer.tsx';
import { EditRole } from './components/EditRole/EditRole.tsx';

import { DialogWithMessage } from '#src/components/DialogWithMessage/DialogWithMessage.tsx';
import { useUserId } from '#src/hooks/auth.tsx';
import { useOrganizations } from '#src/pages/manage/organizations/OrganizationContext.tsx';
import { roleDisplayNames } from '#src/types/users.ts';

interface UserRowProps {
  user: Private.Auth0UserPayload;
  hasWriteAccess: boolean;
  isImpersonating: boolean;
}

const capitalise = (input: string) =>
  input.charAt(0).toUpperCase() + input.slice(1);

export const UserRow = ({ user, hasWriteAccess }: UserRowProps) => {
  const organizations = useOrganizations();
  const activeUserId = useUserId();

  const showToast = useToast();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetMfaDialog, setShowResetMfaDialog] = useState(false);
  const [showEditRoleDialog, setShowEditRoleDialog] = useState(false);

  const { name, id } = user;

  const deleteUser = async () => {
    try {
      await organizations.removeUser(user.id);
      setShowDeleteDialog(false);
    } catch (err) {
      showToast({
        tone: 'critical',
        message: `We couldn’t delete ${name}.`,
        description: String(err),
      });
    }
  };

  const isActingOnSelf = activeUserId === id;
  const possessivePronounOrName = isActingOnSelf ? 'your' : `${name}’s`;

  const resetMfa = async () => {
    try {
      await organizations.resetMfa(user.id);
      setShowResetMfaDialog(false);
      showToast({
        key: 'resetMfa',
        tone: 'positive',
        message: `${capitalise(possessivePronounOrName)} MFA has been reset.`,
        description: `We’ll prompt ${
          isActingOnSelf ? 'you' : 'them'
        } to register a new MFA device on ${
          isActingOnSelf ? 'your' : 'their'
        } next login.`,
      });
    } catch (err) {
      showToast({
        key: 'resetMfa',
        tone: 'critical',
        message: `We couldn’t reset ${possessivePronounOrName} MFA.`,
        description: String(err),
      });
    }
  };

  const baseId = useId();

  return (
    <TableRow>
      <TableCell>
        <Stack space="small">
          <Text weight="strong">{user.name}</Text>
          <Text>{user.emailAddress}</Text>
        </Stack>
      </TableCell>

      <TableCell>
        <Inline space="xxsmall">
          <Text>
            <Badge tone="info">{roleDisplayNames[user.role]}</Badge>
          </Text>
          {user.impersonating ? (
            <Text>
              <Badge tone="positive">Impersonating</Badge>
            </Text>
          ) : null}
        </Inline>
      </TableCell>

      <TableCell>
        <DateRenderer inputDate={user.createdAt} updatedDate={user.updatedAt} />
      </TableCell>

      {hasWriteAccess ? (
        <TableCell align="right">
          {user.connectionType === 'username-password' && (
            <DialogWithMessage
              title="Reset MFA"
              id={`${baseId}-resetMfa`}
              message={`Are you sure you want to reset ${possessivePronounOrName} MFA?`}
              action="Reset MFA"
              showDialog={showResetMfaDialog}
              onCancel={() => setShowResetMfaDialog(false)}
              onConfirm={resetMfa}
            />
          )}

          <DialogWithMessage
            title={`Delete ${name}`}
            id={`${baseId}-delete`}
            message={`Are you sure you want to delete ${name}? This cannot be undone.`}
            action="Delete"
            showDialog={showDeleteDialog}
            onCancel={() => setShowDeleteDialog(false)}
            onConfirm={deleteUser}
          />

          <Dialog
            id={`${baseId}-editRole`}
            onClose={() => setShowEditRoleDialog(false)}
            open={showEditRoleDialog}
            title="Edit role"
          >
            <EditRole user={user} onSave={() => setShowEditRoleDialog(false)} />
          </Dialog>

          <Box display="flex" gap="small" alignItems="center">
            {user.connectionType === 'username-password' && (
              <ButtonIcon
                id={`${baseId}-resetMfa-button-icon`}
                label="Reset MFA"
                icon={<IconMobile />}
                variant="transparent"
                size="large"
                onClick={() => setShowResetMfaDialog(true)}
              />
            )}

            {!isActingOnSelf ? (
              <>
                <ButtonIcon
                  id={`${baseId}-editRole-button-icon`}
                  label="Edit role"
                  icon={<IconSecurity />}
                  variant="transparent"
                  size="large"
                  onClick={() => setShowEditRoleDialog(true)}
                />

                <ButtonIcon
                  id={`${baseId}-removeUser-button-icon`}
                  label="Remove User"
                  icon={<IconDelete />}
                  variant="transparent"
                  size="large"
                  onClick={() => setShowDeleteDialog(true)}
                />
              </>
            ) : null}
          </Box>
        </TableCell>
      ) : null}
    </TableRow>
  );
};
